/**
 * Self Memory Panel
 *
 * Displays the AI therapist's memory about the user themselves.
 * Thin wrapper around the shared MemoryCard / EmptyMemoryCard components.
 * Used on the Account page under the "therapy" tab.
 */

import { useState } from "react";
import { Card } from "../ui/card";
import { Loader2 } from "lucide-react";
import { MemoryCard } from "../memory/MemoryCard";
import { EmptyMemoryCard } from "../memory/EmptyMemoryCard";
import { EditSelfMemoryModal } from "./EditSelfMemoryModal";
import {
  useUserSelfMemory,
  useResetUserSelfMemory,
  useRegenerateUserSelfMemory,
  useClearUserSelfMemory,
} from "../../hooks/useSpeakerMemories";

export function SelfMemoryPanel() {
  const { data: memory, isLoading, error, refetch } = useUserSelfMemory();
  const [showEditModal, setShowEditModal] = useState(false);
  const reset = useResetUserSelfMemory();
  const regenerate = useRegenerateUserSelfMemory();
  const clear = useClearUserSelfMemory();

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading your memory...</span>
        </div>
      </Card>
    );
  }

  // Regeneration is async on the server: the mutation returns 202 immediately
  // and the worker writes the new content later. Use the server-derived flag
  // (with the mutation pending state as a brief stand-in) so the UI keeps
  // spinning until the worker actually finishes.
  const isRegenerating =
    regenerate.isPending || (memory?.regeneration_in_progress ?? false);

  if (error || !memory) {
    return (
      <EmptyMemoryCard
        title="Your Memory"
        description="No self-memory yet. The AI therapist will build a profile about your communication patterns after your first reflection is processed."
        onRegenerate={async () => {
          await regenerate.mutateAsync();
          void refetch();
        }}
        regeneratePending={isRegenerating}
      />
    );
  }

  return (
    <>
      <MemoryCard
        title="Your Memory"
        memory={memory}
        onEdit={() => setShowEditModal(true)}
        onReset={async () => {
          await reset.mutateAsync();
          void refetch();
        }}
        onRegenerate={async () => {
          await regenerate.mutateAsync();
          void refetch();
        }}
        onClear={async () => {
          await clear.mutateAsync();
          void refetch();
        }}
        resetPending={reset.isPending}
        regeneratePending={isRegenerating}
        clearPending={clear.isPending}
      />
      <EditSelfMemoryModal
        open={showEditModal}
        onOpenChange={setShowEditModal}
        currentContent={memory.content}
        onSuccess={() => refetch()}
      />
    </>
  );
}
